import { OG_IMAGE_PATH, SITE_URL } from './jsonld';

type JsonLd = Record<string, unknown>;

/**
 * Builds a TanStack Router `head()` result with the full per-page SEO set:
 * title, description, OG/Twitter overrides, canonical, and optional JSON-LD.
 * Root defaults (favicons, fonts, LocalBusiness schema) merge in from
 * routes/__root.tsx; same-name meta here wins. The root deliberately sets no
 * canonical, because `links` are not deduped by `rel` and two would collide.
 */
export const buildHead = ({
  title,
  description,
  path,
  image = `${SITE_URL}${OG_IMAGE_PATH}`,
  jsonLd = []
}: {
  title: string;
  description: string;
  /** Route path beginning with '/', e.g. '/services/concrete-driveways' */
  path: string;
  image?: string;
  jsonLd?: JsonLd[];
}) => ({
  meta: [
    { title },
    { name: 'description', content: description },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:url', content: `${SITE_URL}${path}` },
    { property: 'og:image', content: image },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: image }
  ],
  links: [{ rel: 'canonical', href: `${SITE_URL}${path}` }],
  scripts: jsonLd.map((schema) => ({
    type: 'application/ld+json',
    children: JSON.stringify(schema)
  }))
});

export const breadcrumbJsonLd = (
  crumbs: Array<{ name: string; path: string }>
): JsonLd => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: crumbs.map((crumb, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: crumb.name,
    item: `${SITE_URL}${crumb.path}`
  }))
});

export const serviceJsonLd = ({
  name,
  description,
  path
}: {
  name: string;
  description: string;
  path: string;
}): JsonLd => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name,
  description,
  url: `${SITE_URL}${path}`,
  serviceType: name,
  provider: {
    '@type': 'LocalBusiness',
    name: 'Dreams Built',
    telephone: '+64 21 412 384',
    url: SITE_URL
  },
  areaServed: [
    { '@type': 'City', name: 'Hamilton' },
    { '@type': 'City', name: 'Morrinsville' },
    { '@type': 'City', name: 'Cambridge' },
    { '@type': 'City', name: 'Te Awamutu' },
    { '@type': 'AdministrativeArea', name: 'Waikato' }
  ]
});

export const faqJsonLd = (
  faqs: Array<{ question: string; answer: string }>
): JsonLd => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: { '@type': 'Answer', text: answer }
  }))
});
